import { useEffect, useState } from "react";
import steamSearchService from "../../service/steamSearch.service";
import { pickCards } from "../../utils/pickCards";
import { STEAM_RUB_MAX, STEAM_RUB_MIN, topupRub } from "../steam/SteamTopUpWidget";

export const STEAM_STORE_ID = "steam";
export const USD_CARDS_REGION = "usd";

/** Регионы аккаунта, где работает пополнение по логину. */
export const TOPUP_REGIONS = new Set(["ru", "kz"]);
/** Регионы аккаунта, где можно активировать коды США: Steam сам конвертирует номинал в валюту кошелька. */
export const USD_CODES_REGIONS = new Set(["us", "kz"]);

/** Запас на курс Steam при конвертации долларов в тенге — чтобы кодов точно хватило. */
const KZT_CONVERSION_MARGIN = 1.02;

/** Курсы и комиссия пополнения по логину; settled — оба запроса завершились (даже с ошибкой). */
export function useSteamPricing() {
  const [pricing, setPricing] = useState({ rates: null, markup: null, settled: false });
  useEffect(() => {
    let alive = true;
    Promise.all([steamSearchService.rates(), steamSearchService.topupMarkup()]).then(
      ([rates, markup]) => alive && setPricing({ rates, markup, settled: true }),
    );
    return () => {
      alive = false;
    };
  }, []);
  return pricing;
}

/** Сколько рублей зачислить в Steam под цену игры; тенге — по курсу бэкенда пополнения. */
export function rubForPrice(amount, currency, rates) {
  if (amount == null) return null;
  if (currency === "RUB") return amount;
  if (currency === "KZT" && rates?.KZT && rates?.RUB) {
    return (amount / rates.KZT) * rates.RUB;
  }
  return null;
}

/** Сколько долларов кодов США нужно под цену игры. */
export function usdForPrice(amount, currency, rates) {
  if (amount == null) return null;
  if (currency === "USD") return amount;
  if (currency === "KZT" && rates?.KZT) {
    return Math.ceil(((amount / rates.KZT) * KZT_CONVERSION_MARGIN) * 100) / 100;
  }
  return null;
}

/**
 * Способы купить игру: коды США (план карт) и/или пополнение по логину.
 * best — что дешевле в рублях.
 */
export function buyOptions(game, region, { rates, markup }, usdCards) {
  if (!game?.price) return { codes: null, topup: null, best: null };

  let codes = null;
  if (USD_CODES_REGIONS.has(region) && usdCards?.denominations?.length) {
    const need = usdForPrice(game.price, game.currency, rates);
    const plan = need ? pickCards(need, usdCards.denominations) : null;
    if (plan) codes = { need, plan, cost: plan.rub };
  }

  let topup = null;
  if (TOPUP_REGIONS.has(region)) {
    const rub = rubForPrice(game.price, game.currency, rates);
    if (rub) {
      const amount = topupRub(rub);
      topup = {
        amount,
        exact: game.currency === "RUB",
        belowMin: rub < STEAM_RUB_MIN,
        capped: rub > STEAM_RUB_MAX,
        cost: markup ? Math.round(amount * markup) : null,
      };
    }
  }

  let best = codes ? "codes" : topup ? "topup" : null;
  if (codes && topup?.cost && topup.cost < codes.cost) best = "topup";
  return { codes, topup, best };
}

export const formatRub = (value) => `${Math.ceil(value).toLocaleString("ru-RU")} ₽`;
